import React, { useEffect, useRef, useState } from "react";
import { User } from "firebase/auth";
import { useAuth, LoginOtpChallenge } from "../../context/AuthContext";
import { FaMobileAlt } from "react-icons/fa";

/**
 * The second half of signing in: the code.
 *
 * The password has already been checked at this point, but no session cookie
 * exists yet — the server only issues one once the code is verified, so a
 * stolen password on its own gets nobody in.
 */
const LoginOtpStep: React.FC<{
    user: User;
    challenge: LoginOtpChallenge;
    onVerified: () => void;
    onCancel: () => void;
}> = ({ user, challenge, onVerified, onCancel }) => {
    const { requestLoginOtp, createSessionWithOtp } = useAuth();

    const [code, setCode] = useState("");
    const [challengeId, setChallengeId] = useState(challenge.challengeId ?? "");
    const [sentTo, setSentTo] = useState(challenge.sentTo ?? "your registered number");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(challenge.resendInSeconds ?? 60);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    // Counts down to when another code may be asked for.
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [secondsLeft]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!code.trim() || busy) return;
        setBusy(true);
        setError("");
        const result = await createSessionWithOtp(user, { challengeId, code: code.trim() });
        setBusy(false);

        if (result.ok) return onVerified();

        setError(result.message ?? "That code could not be checked.");
        setCode("");
        inputRef.current?.focus();
    };

    const resend = async () => {
        if (secondsLeft > 0 || busy) return;
        setBusy(true);
        setError("");
        try {
            const next = await requestLoginOtp(user, { resend: true, challengeId });
            if (next.challengeId) setChallengeId(next.challengeId);
            if (next.sentTo) setSentTo(next.sentTo);
            setSecondsLeft(next.resendInSeconds ?? 60);
            setCode("");
            inputRef.current?.focus();
        } catch (err: any) {
            setError(err?.response?.data?.errors?.[0]?.message ?? "Could not send another code.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="w-full">
            <div className="mb-4 flex justify-center">
                <FaMobileAlt className="text-primary" size={30} />
            </div>

            <h2 className="mb-2 text-center text-xl font-bold text-primary">Enter your code</h2>
            <p className="mb-6 text-center text-sm text-gray-500">
                We sent a code to <strong className="text-gray-700">{sentTo}</strong>.
            </p>

            <input
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                placeholder="––––"
                className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-3 text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary"
            />

            {error && <div className="mb-3 text-center text-sm text-red-600">{error}</div>}

            <button
                type="submit"
                disabled={busy || !code.trim()}
                className="w-full rounded-lg bg-primary py-2.5 font-semibold text-white transition hover:bg-blue-800 disabled:opacity-50"
            >
                {busy ? "Checking…" : "Sign in"}
            </button>

            <div className="mt-4 text-center text-sm">
                {secondsLeft > 0 ? (
                    <span className="text-gray-400">
                        You can ask for another code in {secondsLeft}s
                    </span>
                ) : (
                    <button type="button" onClick={resend} disabled={busy} className="text-primary font-medium">
                        Send another code
                    </button>
                )}
            </div>

            <button
                type="button"
                onClick={onCancel}
                className="mt-3 w-full text-center text-xs text-gray-500 underline"
            >
                Use a different account
            </button>
        </form>
    );
};

export default LoginOtpStep;
